import React, { useCallback, useMemo, useState } from "react";
import { Button, Table } from "reactstrap";
import cx from "classnames";
import { ProductSuggestionTableRow } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/ProductResultMainView/ProductSuggestionResultView/ResultingListTable/ProductSuggestionTableItem";

import styles from "../ProductSiggestiomResultView.module.scss";
import { Icon } from "components/Icon/Icon";
import { useSubscription } from "urql";
import { resultingListSubscription } from "api/subscription/product";
import { useProductConfiguratorStore } from "components/_ProductConfigurator/store/ProductConfiguratorStore";
import { useResultingListForm } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/ProductResultMainView/ProductSuggestionResultView/providers/ResultingListFormContextProvider";
import { Spinner, SPINNER_SIZES } from "components/Spinner";
import { getOrderCodeState } from "components/_ProductConfigurator/_helpers";
import { useAppBaseContext } from "providers";
import { useDirtyCheck } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/ProductResultMainView/ProductSuggestionResultView/ResultingListTable/useRequestDirty";
import type { ProductListViewItem } from "components/_ProductConfigurator/store/ProductConfiguration.types";
import { ProductConfiguratorResultSection } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/ProductResultMainView/ProductConfiguratoResultSection";
import { useTranslate } from "hooks";

export const ProductSuggestionResultTable = () => {
  const [isFetching, setIsFetching] = useState(false);
  const { product } = useProductConfiguratorStore.use.productData() || {};
  const { values } = useResultingListForm();
  const requestParams = useProductConfiguratorStore.use.requestParams();

  const translate = useTranslate();

  const { lang } = useAppBaseContext() as { lang: string };

  const targetFilterValues = useMemo(() => {
    return Object.entries(values)
      .filter(([_, value]) => value !== false)
      .map(([id, value]) => ({
        id,
        value: value === true ? "true" : value,
      }));
  }, [values]);

  const variables = useMemo(
    () => ({
      input: {
        seriesId: requestParams?.seriesId,
        strategyId: product?.currentStrategyId,
        state: {
          optionStates: getOrderCodeState(product?.orderCode?.parts),
        },
        targetFilterValues,
        languageIso2L: lang,
        countryIso2L: requestParams?.countryCode,
        requestUnitsSystem: requestParams?.resultUnitsSystem,
        resultUnitsSystem: requestParams?.resultUnitsSystem,
      },
    }),
    [product, requestParams, lang, targetFilterValues],
  );

  const [{ data }, executeSubscription] = useSubscription(
    {
      query: resultingListSubscription,
      variables,
      pause: true,
    },
    (_messages, response) => {
      setIsFetching(false);
      return response;
    },
  );

  const { isDirty, markExecuted } = useDirtyCheck({
    ...variables.input,
    state: null,
  });

  const handleGetList = useCallback(() => {
    setIsFetching(true);
    markExecuted();
    executeSubscription();
  }, [executeSubscription, markExecuted]);

  return (
    <ProductConfiguratorResultSection
      title=""
      classNames={cx(
        styles.acousticCell,
        "border rounded p-2 m-2 m-xxl-3 mt-xxl-0 p-xxl-2",
      )}
      viewWithTable={true}
    >
      {({ isInModal }: { isInModal: boolean }) => (
        <div className="px-3">
          <div className="w-100">
            {!isInModal && (
              <Button
                className="mt-n6 px-4 position-relative"
                color="outline-dark"
                onClick={handleGetList}
                disabled={isFetching}
              >
                <Icon
                  prefix="trox"
                  iconName="arrow-rotate-left-regular"
                  className="me-2"
                />
                {translate("PRODUCT_SUGGESTION_CALCULATE_BTN")}
                {isFetching && (
                  <span className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center">
                    <Spinner size={SPINNER_SIZES.XS} />
                  </span>
                )}
              </Button>
            )}
            <div className="w-100 overflow-auto">
              <Table className={styles.table}>
                <thead className={cx({ "opacity-50": !data || isDirty })}>
                  <tr>
                    {product?.listViewConfiguration?.columnTitles?.map(
                      ({ key, title }) => (
                        <th key={key}>{title}</th>
                      ),
                    )}
                  </tr>
                </thead>
                {data?.productListView?.message && (
                  <div>Something went wrong</div>
                )}
                {data && !isDirty && !isFetching && (
                  <tbody>
                    {data?.productListView?.listViewData?.productListViewItems?.map(
                      (
                        productListViewItem: ProductListViewItem,
                        key: number,
                      ) => (
                        <ProductSuggestionTableRow
                          key={key}
                          {...productListViewItem}
                          currentRowKey={key}
                          columnTitles={
                            data?.productListView?.listViewData?.columnTitles
                          }
                        />
                      ),
                    )}
                  </tbody>
                )}
              </Table>
            </div>
          </div>
        </div>
      )}
    </ProductConfiguratorResultSection>
  );
};
